import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { v4 as uuid } from 'uuid';
import { ChevronUpIcon, UploadIcon } from 'lucide-react';
import { useConfirm } from '@/components/confirm/ConfirmProvider';
import { OptionCombobox } from '@/components/OptionCombobox';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { useApi, useAsync } from '@/lib/legacy';
import { notifyError, notifySuccess } from '@/lib/notify';
import { cn } from '@/lib/utils';
import { Grid as GridJs } from '../../ui/components/Grid';
import { MAX_FILE_SIZE as MAX_FILE_SIZE_JS } from '../../constants';
import { ActionButton, ActionRow, type Row } from '../PolicyPage/tabParts';

/* eslint-disable @typescript-eslint/no-explicit-any -- boundary: ui/components/Grid (AG Grid stays legacy) and its cell params */
const Grid = GridJs as any;
type CellParams = any;
/* eslint-enable @typescript-eslint/no-explicit-any */
const MAX_FILE_SIZE = MAX_FILE_SIZE_JS as number;

type Option = { label: string; value: string };
type AttachmentData = { fileName?: string; entityType?: Option | null; fileType?: Option | null };
type Progress = { status: 'uploading' | 'completed' | 'error'; progress: number; attachmentId?: string; file?: File; error?: string };

const titleCase = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** The file name and the two types every attachment dialog asks for. */
function AttachmentFields({
  data,
  setData,
  fileNameFallback,
  fileNamePlaceholder,
  entityTypeOptions,
  fileTypeOptions,
  onFileTypeChange,
}: {
  data: AttachmentData;
  setData: (update: AttachmentData | ((prev: AttachmentData) => AttachmentData)) => void;
  fileNameFallback: string;
  fileNamePlaceholder: string;
  entityTypeOptions: Option[];
  fileTypeOptions: Option[];
  onFileTypeChange: (option: Option | null) => void;
}) {
  const fileNameId = useId();
  return (
    <>
      <div className="mb-5 flex flex-col gap-2">
        <Label htmlFor={fileNameId} className="font-medium">
          File Name
        </Label>
        <Input
          id={fileNameId}
          value={data.fileName ?? fileNameFallback}
          onChange={(e) => {
            const val = e.target.value;
            setData((prev) => ({ ...prev, fileName: val }));
          }}
          placeholder={fileNamePlaceholder}
        />
      </div>
      <div className="mb-6 flex flex-row gap-4">
        <OptionCombobox
          label="Entity Type"
          className="max-w-[48%] min-w-0 flex-1"
          placeholder="Select entity type"
          options={entityTypeOptions}
          value={data.entityType ?? null}
          onChange={(option) => {
            setData({
              entityType: option,
              fileType: null,
              ...(data.fileName !== undefined ? { fileName: data.fileName } : {}),
            });
          }}
          getLabel={(option) => option?.label || ''}
          isEqual={(option, value) => option?.value === value?.value}
        />
        <OptionCombobox
          label="File Type"
          className="max-w-[48%] min-w-0 flex-1"
          placeholder="Select file type"
          options={fileTypeOptions}
          value={data.fileType ?? null}
          onChange={onFileTypeChange}
          getLabel={(option) => option?.label || ''}
          isEqual={(option, value) => option?.value === value?.value}
        />
      </div>
    </>
  );
}

function AttachmentDialog({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="min-w-[320px] sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="text-center">{title}</DialogTitle>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

/** The folder view's expand/collapse row. */
function FolderToggle({ label, expanded, onToggle, className }: { label: string; expanded: boolean; onToggle: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn('flex cursor-pointer flex-row items-center gap-2 text-[1.1em] font-semibold select-none', className)}
    >
      <ChevronUpIcon aria-hidden className={cn('size-6 transition-transform duration-200', expanded ? 'rotate-180' : 'rotate-90')} />
      {label}
    </button>
  );
}

export const Attachments = ({
  entity,
  entityName,
  setGridReference,
}: {
  entity?: Row;
  entityName: string;
  setGridReference?: (ref: unknown) => void;
}) => {
  const { id: entityId, customerId } = entity || {};
  const [attachments, setAttachments] = useState<Row[]>([]);
  const [inputValue, setInputValue] = useState<File | null>(null);
  const [attachmentTypes, setAttachmentTypes] = useState<Row[]>([]);
  const lastInputValue = useRef(inputValue);
  const dropRef = useRef<HTMLLabelElement>(null);
  const [savingAttachment, setSavingAttachment] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [attachmentData, setAttachmentData] = useState<AttachmentData>({
    entityType: { label: titleCase(entityName), value: entityName },
    fileType: null,
  });
  const [viewMode, setViewMode] = useState<'table' | 'folder'>('table');
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [originalAttachments, setOriginalAttachments] = useState<Row[]>([]);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string | null>(null);
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchFiles, setBatchFiles] = useState<File[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [uploadProgress, setUploadProgress] = useState<Record<number, Progress>>({});
  const [batchUploadComplete, setBatchUploadComplete] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState<Row | null>(null);
  const { uploadAttachment, saveAttachment, getAttachments, getAttachmentTypes, updateAttachment, deleteAttachment, downloadAllAttachments } =
    useApi();
  const confirm = useConfirm();

  useEffect(() => {
    const fetchAttachmentTypes = async () => {
      try {
        const types = await getAttachmentTypes();
        setAttachmentTypes(types);
      } catch (err) {
        console.error('Failed to fetch attachment types:', err);
      }
    };
    fetchAttachmentTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entityTypeOptions = useMemo(() => {
    const uniqueEntityTypes: string[] = [...new Set((attachmentTypes || []).map((type) => type.entityType as string))];
    return uniqueEntityTypes.map((type) => ({
      label: titleCase(type),
      value: type,
    }));
  }, [attachmentTypes]);

  const fileTypeOptions = useMemo(() => {
    if (!attachmentData.entityType) return [];
    return attachmentTypes
      .filter((type) => type.entityType === attachmentData.entityType?.value)
      .map((type) => ({
        label: type.fileType as string,
        value: type.fileType as string,
      }));
  }, [attachmentTypes, attachmentData.entityType]);

  const fetchAttachments = async () => {
    try {
      const fetchedAttachments = await getAttachments(customerId);
      setAttachments(fetchedAttachments);
      setOriginalAttachments(fetchedAttachments);
    } catch (err) {
      console.error('Failed to fetch attachment:', err);
    }
  };

  const validateFileSize = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      notifyError(`File "${file.name}" is too large. Maximum size is 20MB.`);
      return false;
    }
    return true;
  };

  const checkDuplicate = (filename: string | undefined, entityType: string | undefined, fileType: string | undefined) => {
    return attachments.some((att) => att.fileName === filename && att.entityType === entityType && att.fileType === fileType);
  };

  const handleFileValidation = (file: File, onSuccess: (file: File) => void) => {
    if (!validateFileSize(file)) {
      return false;
    }

    onSuccess(file);
    return true;
  };

  const handleDownloadAll = async () => {
    try {
      setIsDownloadingAll(true);
      const response = await downloadAllAttachments(customerId);

      if (response && response.data) {
        const byteCharacters = atob(response.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.filename || `attachments-${customerId}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Download error:', error);
      notifyError('Failed to download attachments');
    } finally {
      setIsDownloadingAll(false);
    }
  };

  useEffect(() => {
    fetchAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadDocument = useCallback(
    async (document: File) => {
      try {
        const id = uuid();
        setAttachmentId(id);
        const response = await uploadAttachment(id, customerId, { document });
        if (response) {
          setShowModal(true);
        }
      } catch (error) {
        notifyError('An error occurred while uploading attachment Please try again.');
        console.error('Failed to upload attachment:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadAttachment]
  );

  const { execute } = useAsync(uploadDocument, { immediate: false });

  useEffect(() => {
    if (inputValue && inputValue !== lastInputValue.current) {
      lastInputValue.current = inputValue;
      setAttachmentData({
        fileName: inputValue?.name,
      });
      execute(inputValue);
    }
  }, [inputValue, execute]);

  const handleDrag = useCallback((e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const processBatchUpload = async (files: File[]) => {
    const uploadPromises = files.map(async (file, index) => {
      try {
        setUploadProgress((prev) => ({ ...prev, [index]: { status: 'uploading', progress: 0 } }));

        const id = uuid();
        await uploadAttachment(id, customerId, { document: file });

        setUploadProgress((prev) => ({
          ...prev,
          [index]: { status: 'completed', progress: 100, attachmentId: id, file },
        }));

        return { success: true, file, attachmentId: id, index };
      } catch (error) {
        setUploadProgress((prev) => ({
          ...prev,
          [index]: { status: 'error', progress: 0, error: (error as Error).message },
        }));
        return { success: false, file, error, index };
      }
    });

    await Promise.all(uploadPromises);
    setBatchUploadComplete(true);
  };

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);

      if (files.length === 1) {
        handleFileValidation(files[0] as File, (validatedFile) => setInputValue(validatedFile));
      } else {
        const validFiles: File[] = [];

        for (const file of files) {
          handleFileValidation(file, (validatedFile) => validFiles.push(validatedFile));
        }

        if (validFiles.length === 0) return;

        setBatchFiles(validFiles);
        setCurrentFileIndex(0);
        setUploadProgress({});
        setBatchUploadComplete(false);
        setShowBatchModal(true);
        processBatchUpload(validFiles);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBatchNext = () => {
    if (currentFileIndex < batchFiles.length - 1) {
      setCurrentFileIndex(currentFileIndex + 1);
    } else {
      setShowBatchModal(false);
      setBatchFiles([]);
      setCurrentFileIndex(0);
      setUploadProgress({});
      fetchAttachments();
    }
  };

  // Saving a file whose name and types match one already here makes a new version of it; ask first.
  const confirmNewVersion = (fileName: string | undefined) =>
    confirm({
      title: `A file with the name "${fileName}" already exists for this ${attachmentData.entityType?.label} and file type.`,
      description: 'If you continue, it will create a new version of this file. Do you want to continue?',
    });

  const handleBatchSave = async () => {
    const currentFile = batchFiles[currentFileIndex];
    const progressInfo = uploadProgress[currentFileIndex];

    try {
      setSavingAttachment(true);

      if (!attachmentData?.fileType) {
        notifyError('Please enter a file type.');
        setSavingAttachment(false);
        return;
      }

      const finalFileName = attachmentData?.fileName ?? currentFile?.name;

      if (checkDuplicate(finalFileName, attachmentData.entityType?.value, attachmentData.fileType.value)) {
        if (!(await confirmNewVersion(finalFileName))) {
          setSavingAttachment(false);
          return;
        }
      }

      // Proceed with save
      const payload = {
        customerId: entity?.customerId,
        entityType: attachmentData?.entityType?.value,
        entityId: entityId,
        fileType: attachmentData?.fileType?.value,
        fileName: finalFileName,
        contentType: currentFile?.type,
        id: progressInfo?.attachmentId,
      };

      const response = await saveAttachment(payload);
      if (response) {
        handleBatchNext();
        setAttachmentData({
          entityType: {
            label: titleCase(entityName),
            value: entityName,
          },
          fileType: null,
        });
      }
    } catch (error) {
      notifyError('An error occurred while saving attachment. Please try again.');
      console.error('Error saving attachment:', error);
    } finally {
      setSavingAttachment(false);
    }
  };

  const handleModalSave = async () => {
    try {
      setSavingAttachment(true);

      if (!attachmentData?.fileType) {
        notifyError('Please enter a file type.');
        setSavingAttachment(false);
        return;
      }

      const finalFileName = attachmentData?.fileName ?? inputValue?.name;

      if (checkDuplicate(finalFileName, attachmentData.entityType?.value, attachmentData.fileType.value)) {
        if (!(await confirmNewVersion(finalFileName))) {
          setSavingAttachment(false);
          return;
        }
      }

      const payload = {
        customerId: entity?.customerId,
        entityType: attachmentData?.entityType?.value,
        entityId: entityId,
        fileType: attachmentData?.fileType?.value,
        fileName: finalFileName,
        contentType: inputValue?.type,
        id: attachmentId,
      };

      const response = await saveAttachment(payload);
      if (response) {
        const refreshed = await getAttachments(customerId);
        setAttachments(refreshed);
        setShowModal(false);
        setInputValue(null);
        setAttachmentData({});
      }
    } catch (error) {
      notifyError('An error occurred while saving attachment. Please try again.');
      console.error('Error saving attachment:', error);
    } finally {
      setSavingAttachment(false);
    }
  };

  const handleEditAttachment = (attachment: Row) => {
    setEditingAttachment(attachment);
    setAttachmentData({
      fileName: attachment.fileName,
      entityType: {
        label: titleCase(attachment.entityType),
        value: attachment.entityType,
      },
      fileType: {
        label: attachment.fileType,
        value: attachment.fileType,
      },
    });
    setShowEditModal(true);
  };

  const handleUpdateSingleAttachment = async () => {
    try {
      setSavingAttachment(true);
      if (!attachmentData?.fileType || !attachmentData?.entityType) {
        notifyError('Please fill in all required fields.');
        return;
      }
      const finalFileName = attachmentData?.fileName;
      if (
        finalFileName !== editingAttachment?.fileName &&
        checkDuplicate(finalFileName, attachmentData.entityType.value, attachmentData.fileType.value)
      ) {
        const overwrite = await confirm({ title: `A file with the name "${finalFileName}" already exists. Do you want to overwrite it?` });
        if (!overwrite) {
          return;
        }
      }
      const updatedAttachment = {
        id: editingAttachment?.id,
        fileName: finalFileName,
        entityType: attachmentData.entityType.value,
        fileType: attachmentData.fileType.value,
        metadata: editingAttachment?.metadata || {},
      };
      await updateAttachment([updatedAttachment]);
      await fetchAttachments();

      setShowEditModal(false);
      setEditingAttachment(null);
      setAttachmentData({});
      notifySuccess('Attachment updated successfully.');
    } catch (error) {
      notifyError('An error occurred while updating attachment. Please try again.');
      console.error('Error updating attachment:', error);
    } finally {
      setSavingAttachment(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setIsSavingNotes(true);
      const changedAttachments = attachments.filter((att, index) => {
        const original = originalAttachments[index];
        return att.metadata?.notes !== original?.metadata?.notes;
      });

      if (changedAttachments.length === 0) return;

      await updateAttachment(changedAttachments);
      notifySuccess('Saved successfully.');
      await fetchAttachments();
    } catch (error) {
      console.error('Error saving notes:', error);
      notifyError('Failed to save notes. Please try again.');
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleDeleteAttachment = async () => {
    if (!selectedAttachmentId) {
      notifyError('Please select an attachment to delete.');
      return;
    }
    setIsDeleteInProgress(true);
    try {
      await deleteAttachment(selectedAttachmentId);
      await fetchAttachments();
      setSelectedAttachmentId(null);
    } catch (error) {
      console.log(error);
      notifyError('Failed to delete attachment. Please try again.');
    } finally {
      setIsDeleteInProgress(false);
    }
  };

  useEffect(() => {
    const div = dropRef.current;
    if (div) {
      div.addEventListener('dragenter', handleDrag);
      div.addEventListener('dragleave', handleDrag);
      div.addEventListener('dragover', handleDrag);
      div.addEventListener('drop', handleDrop);
      return () => {
        div.removeEventListener('dragenter', handleDrag);
        div.removeEventListener('dragleave', handleDrag);
        div.removeEventListener('dragover', handleDrag);
        div.removeEventListener('drop', handleDrop);
      };
    }
    return undefined;
  }, [handleDrag, handleDrop]);

  const formatDateForFilename = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const populateNamingConvention = (template: string, namedEntity: Row, fileType: string, originalFileName: string) => {
    if (!template) return originalFileName;
    let result = template;
    if (namedEntity.policyNumber) {
      result = result.replace(/{policy_number}/g, namedEntity.policyNumber);
    }
    if (namedEntity.quoteNumber) {
      result = result.replace(/{quote_number}/g, namedEntity.quoteNumber.toString());
    }
    if (namedEntity.insured) {
      const cleanInsured = namedEntity.insured.replace(/[^a-zA-Z0-9]/g, '_');
      result = result.replace(/{insured}/g, cleanInsured);
    }
    if (namedEntity.effectiveAt) {
      const formattedDate = formatDateForFilename(namedEntity.effectiveAt);
      result = result.replace(/{policy\.effective_at}/g, formattedDate);
    }
    if (namedEntity.coverageAt) {
      const formattedDate = formatDateForFilename(namedEntity.coverageAt);
      result = result.replace(/{quote\.coverage_at}/g, formattedDate);
    }
    if (fileType) {
      result = result.replace(/{file_type}/g, fileType);
    }
    const extension = originalFileName.substring(originalFileName.lastIndexOf('.'));
    result = result + extension;

    return result;
  };

  // Choosing a file type names the file by that type's naming convention, when it has one.
  const fileTypeChanged = (originalFileName: string | undefined) => (option: Option | null) => {
    setAttachmentData((data) => ({
      ...data,
      fileType: option,
    }));
    if (option) {
      const selectedType = attachmentTypes.find((t) => t.entityType === attachmentData.entityType?.value && t.fileType === option.value);

      if (selectedType?.namingConvention) {
        const newFileName = populateNamingConvention(selectedType.namingConvention, entity || {}, option.value, originalFileName as string);
        setAttachmentData((data) => ({
          ...data,
          fileType: option,
          fileName: newFileName,
        }));
      }
    }
  };

  const columns = [
    {
      headerName: '',
      maxWidth: 60,
      suppressRowClickSelection: true,
      pinned: 'left',
      cellRenderer: (params: CellParams) => {
        return (
          <div className="flex h-full w-full cursor-pointer flex-row items-center justify-center gap-2 text-base text-mce-expander">
            <div className="pl-[15px]">
              <input
                type="radio"
                aria-label={`Select ${params.data.fileName}`}
                name="attachmentId"
                checked={selectedAttachmentId === params.data.id}
                onChange={() => setSelectedAttachmentId(params.data.id)}
                className="m-0 scale-[1.3] cursor-pointer accent-primary"
              />
            </div>
          </div>
        );
      },
    },
    {
      headerName: 'File Name',
      field: 'fileName',
      valueGetter: (params: CellParams) => params.data?.fileName || '',
      pinned: 'left',
    },
    {
      headerName: 'File Type',
      field: 'fileType',
      valueGetter: (params: CellParams) => params.data?.fileType || '',
    },
    {
      headerName: 'Version',
      field: 'version',
      valueGetter: (params: CellParams) => params.data?.version || '',
    },
    {
      headerName: 'Entity Type',
      field: 'entityType',
      valueGetter: (params: CellParams) => params.data?.entityType || '',
    },
    {
      headerName: 'Uploaded By',
      field: 'username',
      valueGetter: (params: CellParams) => params.data?.username || '-',
    },
    {
      headerName: 'Uploaded At',
      field: 'uploadedAt',
      valueGetter: (params: CellParams) => params.data?.uploadedAt,
      valueFormatter: (p: CellParams) => (p.value ? new Date(p.value).toLocaleString() : '-'),
    },
    {
      headerName: 'Updated At',
      field: 'updatedAt',
      valueGetter: (params: CellParams) => params.data?.updatedAt,
      valueFormatter: (p: CellParams) => (p.value ? new Date(p.value).toLocaleString() : '-'),
    },
    {
      headerName: 'Notes',
      field: 'notes',
      singleClickEdit: true,
      editable: true,
      valueGetter: (params: CellParams) => params.data?.metadata?.notes || '',
      valueSetter: (params: CellParams) => {
        const updatedAttachments = attachments.map((att) => (att.id === params.data.id ? { ...att, metadata: { notes: params.newValue } } : att));
        setAttachments(updatedAttachments);
      },
    },
    {
      headerName: 'Download Link',
      field: 'downloadUrl',
      minWidth: 450,
      valueGetter: (params: CellParams) => params.data?.downloadUrl || '',
      cellRenderer: function (url: CellParams) {
        return (
          <a href={url.value} target="_blank" rel="noopener noreferrer">
            {url.value}
          </a>
        );
      },
    },
  ];

  const groupedByEntity = (attachments || []).reduce<Record<string, { entityNumber: string; attachments: Row[] }>>((acc, att) => {
    const groupId = att.entityId;
    if (!acc[groupId]) {
      const entityNumber: string = att.entityNumber || 'Unknown';
      const kind = entityNumber.startsWith('DOL') ? 'Policy' : 'Quote';
      acc[groupId] = {
        entityNumber: `${kind} ${entityNumber}`,
        attachments: [],
      };
    }
    acc[groupId].attachments.push(att);
    return acc;
  }, {});

  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  const saveDisabled = !attachmentData.entityType || !attachmentData.fileType || savingAttachment;

  return (
    <div className="flex flex-col">
      <div className="relative flex flex-col items-center justify-center pt-2.5">
        {/* The drop zone: drop files on it, or click it to choose one. */}
        <label
          ref={dropRef}
          className="flex h-[240px] w-full max-w-[552px] cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-action-border bg-dropzone-bg text-text-subdued"
        >
          <UploadIcon aria-hidden className="size-10 text-primary opacity-60" />
          <span className="text-sm">Drop or select a file</span>
          <Input
            type="file"
            multiple
            accept="*"
            aria-label="Upload file"
            className="sr-only"
            onChange={(e) => {
              const files = e.target.files;
              if (files && files.length) {
                handleFileValidation(files[0] as File, (validatedFile) => setInputValue(validatedFile));
              }
            }}
          />
        </label>
        <div className="mt-4 flex flex-row items-center gap-2.5">
          <p className="min-h-[22px] text-[0.95rem] text-black">{inputValue && inputValue.name ? inputValue.name : 'No files added yet.'}</p>
        </div>
      </div>
      <Separator className="mt-3 bg-rule-subtle" />

      <div className="my-4 ml-2.5 flex flex-row gap-3">
        <Button variant={viewMode === 'table' ? 'default' : 'outline'} onClick={() => setViewMode('table')}>
          Table View
        </Button>
        <Button variant={viewMode === 'folder' ? 'default' : 'outline'} onClick={() => setViewMode('folder')}>
          Folder View
        </Button>
      </div>
      {viewMode === 'table' ? (
        <div className="mb-2.5 max-h-[500px] w-full overflow-y-auto">
          <Grid
            data={attachments}
            columns={columns}
            setGridReference={setGridReference}
            onRowDoubleClicked={(event: CellParams) => handleEditAttachment(event.data)}
            autoSizeStrategy={{
              type: 'fitCellContents',
              defaultMinWidth: 80,
              defaultMaxWidth: 500,
            }}
            defaultColDef={{
              resizable: true,
              sortable: true,
            }}
            floatingFilter={false}
            shouldShowPagination={false}
            defaultSortModel={[
              {
                colId: 'uploadedAt',
                sort: 'desc',
              },
            ]}
          />
        </div>
      ) : (
        <div className="mb-[120px] min-h-[120px] w-full">
          <FolderToggle label="Attachments" expanded={!!expandedFolders['root']} onToggle={() => toggleFolder('root')} className="mb-4" />

          {expandedFolders['root'] && (
            <div className="ml-8">
              {Object.entries(groupedByEntity).map(([groupId, { entityNumber, attachments: files }]) => (
                <div key={groupId} className="mb-4">
                  <FolderToggle label={entityNumber} expanded={!!expandedFolders[groupId]} onToggle={() => toggleFolder(groupId)} />

                  {expandedFolders[groupId] && (
                    <div className="mt-2 ml-8">
                      {files.map((att) => (
                        <div key={att.id} className="mb-2 flex flex-row items-center gap-4">
                          <a href={att.downloadUrl} download className="font-medium text-folder-link no-underline">
                            {att.fileName}
                          </a>
                          <span className="text-folder-type">{`(${att.fileType})`}</span>
                          <span className="text-folder-date">{new Date(att.uploadedAt).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AttachmentDialog open={showModal} onClose={() => setShowModal(false)} title="Attachment uploaded successfully">
        <p className="text-center text-dialog-subtitle">{inputValue?.name ?? ''}</p>
        <AttachmentFields
          data={attachmentData}
          setData={setAttachmentData}
          fileNameFallback={inputValue?.name ?? ''}
          fileNamePlaceholder="Will be populated based on naming convention"
          entityTypeOptions={entityTypeOptions}
          fileTypeOptions={fileTypeOptions}
          onFileTypeChange={fileTypeChanged(inputValue?.name)}
        />
        <DialogFooter className="sm:justify-start">
          <Button onClick={handleModalSave} disabled={saveDisabled}>
            {savingAttachment ? <Spinner /> : null}
            Save Attachment
          </Button>
          <Button variant="outline" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </AttachmentDialog>

      <AttachmentDialog open={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Attachment">
        <AttachmentFields
          data={attachmentData}
          setData={setAttachmentData}
          fileNameFallback=""
          fileNamePlaceholder="Enter file name"
          entityTypeOptions={entityTypeOptions}
          fileTypeOptions={fileTypeOptions}
          onFileTypeChange={fileTypeChanged(editingAttachment?.fileName)}
        />
        <DialogFooter className="sm:justify-start">
          <Button onClick={handleUpdateSingleAttachment} disabled={saveDisabled}>
            {savingAttachment ? <Spinner /> : null}
            Update Attachment
          </Button>
          <Button variant="outline" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </AttachmentDialog>

      <AttachmentDialog
        open={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        title={
          !batchUploadComplete
            ? `Uploading Files (${Object.keys(uploadProgress).length} of ${batchFiles.length})`
            : `Configure File ${currentFileIndex + 1} of ${batchFiles.length}`
        }
      >
        {!batchUploadComplete ? (
          <div className="min-w-0 sm:min-w-[560px]">
            {batchFiles.map((file, index) => (
              <div key={index} className="mb-3 rounded-sm border border-upload-row-border p-3">
                <div className="flex flex-row items-center justify-between">
                  <span className="text-sm font-medium">{file.name}</span>
                  <span>
                    {uploadProgress[index]?.status === 'uploading' && <span className="text-[orange]">Uploading...</span>}
                    {uploadProgress[index]?.status === 'completed' && <span className="text-[green]"> Completed</span>}
                    {uploadProgress[index]?.status === 'error' && <span className="text-[red]">Error</span>}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="min-w-0 sm:min-w-[560px]">
            <p className="text-center text-dialog-subtitle">{batchFiles[currentFileIndex]?.name}</p>
            <AttachmentFields
              data={attachmentData}
              setData={setAttachmentData}
              fileNameFallback={batchFiles[currentFileIndex]?.name ?? ''}
              fileNamePlaceholder="Enter file name"
              entityTypeOptions={entityTypeOptions}
              fileTypeOptions={fileTypeOptions}
              onFileTypeChange={fileTypeChanged(batchFiles[currentFileIndex]?.name)}
            />
            <DialogFooter className="sm:justify-start">
              <Button onClick={handleBatchSave} disabled={saveDisabled}>
                {savingAttachment ? <Spinner /> : null}
                {currentFileIndex < batchFiles.length - 1 ? 'Save & Next' : 'Save & Finish'}
              </Button>
              <Button variant="outline" onClick={() => setShowBatchModal(false)}>
                Cancel Batch
              </Button>
            </DialogFooter>
          </div>
        )}
      </AttachmentDialog>

      {viewMode === 'table' && (
        <ActionRow className="mb-5">
          <ActionButton onClick={handleSaveNotes} loading={isSavingNotes}>
            Save
          </ActionButton>
          {!!attachments.length && (
            <ActionButton onClick={handleDownloadAll} loading={isDownloadingAll}>
              Download All
            </ActionButton>
          )}
          {!!attachments.length && (
            <ActionButton tone="danger" className="ml-auto border-action-border" onClick={handleDeleteAttachment} loading={isDeleteInProgress}>
              Delete
            </ActionButton>
          )}
        </ActionRow>
      )}
    </div>
  );
};
